import type { ReactNode } from 'react';
import { SidebarProvider, useSidebar } from '../../vendor/tailadmin/context/SidebarContext';
import Backdrop from '../../vendor/tailadmin/layout/Backdrop';
import { Breadcrumbs } from '../nav/breadcrumbs';
import { AppHeader } from './app-header';
import { AppSidebar } from './app-sidebar';
import { DeploymentInfo } from './deployment-info';

/**
 * App layout — fork of `vendor/tailadmin/layout/AppLayout.tsx`.
 *
 * MODIFIED FROM UPSTREAM:
 *   - `Outlet` from `react-router` → children prop (AppShell already wires
 *     the TanStack `<Outlet>` via `route-tree.tsx` → `AppShell({ children })`).
 *   - `AppSidebar` / `AppHeader` imports retargeted to local forks under
 *     `components/layout/` (role-aware nav, TanStack routing, our user
 *     dropdown). Vendor `layout/AppSidebar.tsx` + `layout/AppHeader.tsx`
 *     remain pristine for upstream diff.
 *   - Breadcrumbs row added above `{children}` (vendor has no breadcrumb
 *     concept). The Back button used to sit here too, but moved into
 *     `AppHeader` (sticky) so it stays visible while scrolling — #72190.
 *   - Vendor `Backdrop` reused as-is (no react-router import, no
 *     modifications needed).
 *
 * Layout shape (matches upstream + breadcrumb row):
 *   ┌──────────────────────────────────────────────┐
 *   │ Sidebar (fixed left, collapsible+hoverable)  │
 *   ├────────────┬─────────────────────────────────┤
 *   │            │ Header (sticky top) [← Zpět]    │
 *   │            ├─────────────────────────────────┤
 *   │            │ Breadcrumbs                     │
 *   │            │ {children} (main content)       │
 *   └────────────┴─────────────────────────────────┘
 */
function LayoutContent({ children }: { children: ReactNode }) {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar();

  return (
    <div className="min-h-screen xl:flex">
      <div>
        <AppSidebar />
        <Backdrop />
      </div>
      <div
        className={`flex-1 transition-all duration-300 ease-in-out ${
          isExpanded || isHovered ? 'lg:ml-[290px]' : 'lg:ml-[90px]'
        } ${isMobileOpen ? 'ml-0' : ''}`}
      >
        <AppHeader />
        <div className="mx-auto max-w-[1600px] p-4 md:p-6">
          <Breadcrumbs />
          <main className="mt-4">{children}</main>
          <DeploymentInfo />
        </div>
      </div>
    </div>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  return (
    <SidebarProvider>
      <LayoutContent>{children}</LayoutContent>
    </SidebarProvider>
  );
}
